import { selectArtboard } from 'Renderer/domOperations/artboardOperations';
import { getAdStates, getSelectedAdstate } from 'Selectors';
import LayoutRecord from 'PreviewRecords/LayoutRecord';
import AdStateContainerRecord from 'PreviewRecords/AdStateContainerRecord';
import { Map, List } from 'immutable';
import {
  BOUND_PADDING,
  ARTBOARD_NAME_HEIGHT,
  HANDLE_HEIGHT,
  SETTINGS_PANEL_WIDTH,
  MAX_HEIGHT,
  DEVICE_ORIENTATION,
  BOUNDS,
  DEVICE,
  SCREEN,
  IMAGE,
  DIMENSIONS,
  FIT_TO_SCREEN,
  CANVAS_SCALE,
} from 'PreviewConstants/';
import {
  TOPBAR_HEIGHT,
  KEY,
  UI,
  WIDTH,
  HEIGHT,
  SELECTED,
  VIEWPORT,
  ADSTATES_PATH,
  ADSTATE_CONTAINERS_PATH,
} from 'SharedConstants/';

export const getContainerBounds = () => {
  const maxHeight = window.innerHeight
  - TOPBAR_HEIGHT
  - ARTBOARD_NAME_HEIGHT
  - HANDLE_HEIGHT
  - (BOUND_PADDING * 2);

  const maxWidth = window.innerWidth
  - HANDLE_HEIGHT
  - SETTINGS_PANEL_WIDTH
  - (BOUND_PADDING * 2);

  return Map({
    maxWidth,
    maxHeight,
  });
};

export const getCanvasScale = (state) => {
  const maxHeight = state.getIn([UI, BOUNDS, MAX_HEIGHT]);
  const { viewport } = getSelectedAdstate(state);
  const artboardHeight = viewport.get(HEIGHT);
  const device = state.getIn([UI, DEVICE]);
  const orientation = state.getIn([UI, DEVICE_ORIENTATION]);
  const height = device ? device.getIn([orientation, IMAGE, HEIGHT]) : artboardHeight;
  const heightMoreThanMax = height > maxHeight;

  return heightMoreThanMax ? maxHeight / height : 1;
};

export const selectArtboardFromState = (state, width, height) => {
  const artboards = getAdStates(state);
  const selectedArtboard = selectArtboard(
    artboards.map(artboard =>
      ({
        [KEY]: artboard.key,
        [VIEWPORT]: {
          [WIDTH]: artboard.getIn([VIEWPORT, WIDTH]),
          [HEIGHT]: artboard.getIn([VIEWPORT, HEIGHT]),
        },
      }),
    ).toArray(),
    [width, height],
  );
  return artboards.find(a => a.key === selectedArtboard.key);
};

export const getDimensionsFromDevice = (state) => {
  const orientation = state.getIn([UI, DEVICE_ORIENTATION]);
  return state.getIn([UI, DEVICE, orientation, SCREEN]);
};

export const updateAdState = (state, artboardKey) => {
  const artboards = getAdStates(state);
  const { key: currentArtboardKey } = getSelectedAdstate(state);
  const activeArtboardKey = artboards.filter(a => a.key === artboardKey).map(artboard => artboard.key).toArray()[0];
  if (currentArtboardKey === activeArtboardKey) return false;
  state.setIn([...ADSTATES_PATH, currentArtboardKey, SELECTED], false);
  state.setIn([...ADSTATES_PATH, activeArtboardKey, SELECTED], true);
  return getSelectedAdstate(state);
};

export const maybeSelectAdState = (state, dimensions) => {
  const { key: currentArtboardKey } = getSelectedAdstate(state);
  const { key: activeArtboardKey } = selectArtboardFromState(
    state,
    dimensions.get(WIDTH),
    dimensions.get(HEIGHT),
  );
  if (currentArtboardKey === activeArtboardKey) return false;
  state.setIn([...ADSTATES_PATH, currentArtboardKey, SELECTED], false);
  state.setIn([...ADSTATES_PATH, activeArtboardKey, SELECTED], true);
  return getSelectedAdstate(state);
};

export const makeUpdateDevice = (device, orientation) => (mutable) => {
  mutable.setIn([UI, DEVICE], device);
  mutable.setIn([UI, DEVICE_ORIENTATION], orientation);

  const { viewport } = getSelectedAdstate(mutable);
  const newDimensions = device ? getDimensionsFromDevice(mutable) : viewport;
  mutable.setIn([UI, DIMENSIONS], newDimensions);

  maybeSelectAdState(mutable, newDimensions);

  if (!mutable.getIn([UI, FIT_TO_SCREEN])) {
    return;
  }

  const canvasScale = getCanvasScale(mutable);
  mutable.setIn([UI, CANVAS_SCALE], canvasScale);
};

export const initializeLayout = ({ key, id, name, background, backgroundFileName, backgroundDimensions }) => {
  const layoutToAdd = new LayoutRecord({
    key,
    id,
    name,
    device: 'desktop',
    background,
    backgroundFileName,
    backgroundAlign: 'centre',
    backgroundDimensions: new Map([[0, backgroundDimensions[0]], [1, backgroundDimensions[1]]]),
    backgroundOpacity: 1,
    adStateContainers: List(),
    isCustom: true,
  });

  return layoutToAdd;
};

export const initializeDuplicatedLayout = (key, layout) => {
  const {
    id,
    name,
    device,
    background,
    backgroundFileName,
    backgroundDimensions,
    backgroundOpacity,
  } = layout;
  const layoutToAdd = new LayoutRecord({
    key,
    id,
    name,
    device,
    background,
    backgroundFileName,
    backgroundAlign: 'centre',
    backgroundDimensions: new Map([[0, backgroundDimensions[0]], [1, backgroundDimensions[1]]]),
    backgroundOpacity,
    adStateContainers: List(),
    isCustom: true,
  });

  return layoutToAdd;
};

export const initializeAdContainer = ({ key, dimensions }) => {
  const dimensionsMap = new Map([[0, dimensions[0]], [1, dimensions[1]], [2, dimensions[2]], [3, dimensions[3]]]);
  const adStateContainerToAdd = new AdStateContainerRecord({
    key,
    artboard: '',
    dimensions: dimensionsMap,
  });

  return adStateContainerToAdd;
};

export const initializeDuplicatedAdContainer = ({ key, dimensions, artboard, isFixed }) => {
  const dimensionsMap = new Map([[0, dimensions[0]], [1, dimensions[1]], [2, dimensions[2]], [3, dimensions[3]]]);
  const adStateContainerToAdd = new AdStateContainerRecord({
    key,
    artboard,
    dimensions: dimensionsMap,
    isFixed,
  });

  return adStateContainerToAdd;
};

export const getLeftPositionForAdStateContainer = () => {
  const siteBackground = document.querySelector('.site-background');
  if (siteBackground) return siteBackground.offsetWidth / 2;
  return 0;
};

export const getNextAvailableAdStateContainerKey = (state) => {
  const adStateContainers = state.getIn([...ADSTATE_CONTAINERS_PATH]);
  const filteredAdStateContainers = adStateContainers.filter(adStateContainer => typeof (adStateContainer.key) !== 'undefined');
  const adStateContainerKeys = filteredAdStateContainers.map(adStateContainer => adStateContainer.key).toIndexedSeq().toArray();
  const maxKey = (adStateContainerKeys.length === 0) ? -1 : Math.max(...adStateContainerKeys);
  return (Number(maxKey) + 1).toString();
};



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Preview/reducers/utils.js